/**
 * EmployeesPage — J-2. 직원 관리 (마스터 전용)
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { BadgeStatus } from '@indipia/ui-core';
import { Avatar, Badge, Button, Card, Checkbox, EmptyState, ErrorState, Page, PageHeader, Skeleton } from '@indipia/ui-core';
import { useAppStore } from '@indipia/shared';
import { fetchEmployeeList, updateEmployeeStatus } from '../api/employees';
import type { EmployeeItem, StaffStatus } from '../api/employees';

// ── 상태 표시 설정 ────────────────────────────────────────────────────────────
// 배지 색은 ZEU_STAFF_STATUS에 없는 UI 전용 값이라 여기서 관리한다(D-09 §15.5 — Badge의
// status 계약이 색을 소유). 라벨은 공통코드(GET /indipia/init, ZEU_STAFF_STATUS)에서
// 가져오고, 조회 실패·미시딩일 때만 아래 폴백 문구를 쓴다(useStaffStatusLabel).

const STATUS_BADGE: Record<StaffStatus, BadgeStatus> = {
  PENDING:  'warning',
  ACTIVE:   'success',
  REJECTED: 'danger',
  RELEASED: 'mute',
};
const STATUS_LABEL_FALLBACK: Record<StaffStatus, string> = {
  PENDING:  '신청',
  ACTIVE:   '소속 중',
  REJECTED: '반려',
  RELEASED: '소속 해제',
};

function useStaffStatusLabel() {
  const allCodes = useAppStore((s) => s.codes);
  return (status: StaffStatus) =>
    allCodes.find((c) => c.codeGroupCode === 'ZEU_STAFF_STATUS' && c.codeValue === status)?.codeName
      ?? STATUS_LABEL_FALLBACK[status];
}

// ── 직원 카드 ─────────────────────────────────────────────────────────────────

function EmployeeCard({
  emp,
  onApprove,
  onReject,
  onRelease,
  isPending,
}: {
  emp: EmployeeItem;
  onApprove?: () => void;
  onReject?: () => void;
  onRelease?: () => void;
  isPending: boolean;
}) {
  const label = useStaffStatusLabel()(emp.staffStatus);
  const init  = (emp.memberName ?? '?').charAt(0);

  return (
    <Card>
      <div className="flex items-center gap-3">
      {/* 아바타 */}
      <Avatar className="shrink-0">{init}</Avatar>

      {/* 정보 */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-text">{emp.memberName}</span>
          <Badge status={STATUS_BADGE[emp.staffStatus]}>{label}</Badge>
        </div>
        <p className="mt-0.5 text-xs text-text-muted">{emp.loginId}</p>
      </div>

      {/* 액션 버튼 */}
      <div className="flex shrink-0 gap-1.5">
        {emp.staffStatus === 'PENDING' && (
          <>
            {/* 거절·소속해제는 뉴트럴 액션 → etc. 주 액션(승인)을 오른쪽 끝에 둔다 */}
            <Button variant="etc" size="sm" onClick={onReject} disabled={isPending}>
              거절
            </Button>
            <Button variant="primary" size="sm" onClick={onApprove} disabled={isPending}>
              승인
            </Button>
          </>
        )}
        {emp.staffStatus === 'ACTIVE' && (
          <Button variant="etc" size="sm" onClick={onRelease} disabled={isPending}>
            소속 해제
          </Button>
        )}
      </div>
      </div>
    </Card>
  );
}

// ── 그룹 섹션 ─────────────────────────────────────────────────────────────────

function GroupSection({
  label,
  items,
  onApprove,
  onReject,
  onRelease,
  pendingRoleId,
}: {
  label: string;
  items: EmployeeItem[];
  onApprove?: (roleId: number) => void;
  onReject?: (roleId: number) => void;
  onRelease?: (roleId: number) => void;
  pendingRoleId: number | null;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <p className="text-caption font-bold uppercase tracking-widest text-text-muted">{label}</p>
        <Badge status="accent">{items.length}</Badge>
      </div>
      <div className="space-y-2">
        {items.map((emp) => (
          <EmployeeCard
            key={emp.roleId}
            emp={emp}
            isPending={pendingRoleId === emp.roleId}
            onApprove={onApprove ? () => onApprove(emp.roleId) : undefined}
            onReject={onReject   ? () => onReject(emp.roleId)  : undefined}
            onRelease={onRelease ? () => onRelease(emp.roleId) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────

export function EmployeesPage() {
  const queryClient = useQueryClient();
  const [hideReleased, setHideReleased] = useState(true);
  const [pendingRoleId, setPendingRoleId] = useState<number | null>(null);
  const staffStatusLabel = useStaffStatusLabel();

  const { data: employees = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['employees'],
    queryFn: fetchEmployeeList,
  });

  const pending  = employees.filter((e) => e.staffStatus === 'PENDING');
  const active   = employees.filter((e) => e.staffStatus === 'ACTIVE');
  const rejected = employees.filter((e) => e.staffStatus === 'REJECTED');
  const released = employees.filter((e) => e.staffStatus === 'RELEASED');

  const mutation = useMutation({
    mutationFn: ({ roleId, action }: { roleId: number; action: 'approve' | 'reject' | 'release' }) =>
      updateEmployeeStatus(roleId, action),
    onMutate: ({ roleId }) => setPendingRoleId(roleId),
    onSuccess: ({ roleId, staffStatus }) => {
      queryClient.setQueryData(['employees'], (prev: EmployeeItem[] | undefined) =>
        prev?.map((e) => e.roleId === roleId ? { ...e, staffStatus } : e) ?? [],
      );
      const msgs: Partial<Record<StaffStatus, string>> = {
        ACTIVE: '승인이 완료됐어요',
        REJECTED: '거절 처리됐어요',
        RELEASED: '소속 해제됐어요',
      };
      toast.success(msgs[staffStatus] ?? '처리됐어요');
    },
    onError: () => toast.error('처리에 실패했습니다. 다시 시도해주세요.'),
    onSettled: () => setPendingRoleId(null),
  });

  return (
    <Page width="md">
      <PageHeader
        title="직원 관리"
        subtitle={`${staffStatusLabel('ACTIVE')} ${active.length}명 · ${staffStatusLabel('REJECTED')} ${rejected.length}명 · ${staffStatusLabel('RELEASED')} ${released.length}명`}
      />

      {/* 체크박스 */}
      <Checkbox
        className="mb-6"
        checked={hideReleased}
        onChange={(e) => setHideReleased(e.target.checked)}
        label={<span className="text-caption text-text-muted">소속해제된 직원 제외</span>}
      />

      {/* 상태 전환: 로딩(Skeleton) → 실패 ErrorState / 0건 EmptyState / 데이터 Content (D-07 §17) */}
      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <Skeleton key={i} height={66} />)}
        </div>
      ) : isError ? (
        <ErrorState description="직원 목록을 불러오지 못했어요" onRetry={() => refetch()} />
      ) : employees.length === 0 ? (
        <EmptyState title="등록된 직원이 없어요" description="직원이 소속 신청을 하면 여기에 표시돼요" />
      ) : (
        <div className="space-y-6">
          <GroupSection
            label={staffStatusLabel('PENDING')}
            items={pending}
            pendingRoleId={pendingRoleId}
            onApprove={(id) => mutation.mutate({ roleId: id, action: 'approve' })}
            onReject={(id)  => mutation.mutate({ roleId: id, action: 'reject'  })}
          />
          <GroupSection
            label={staffStatusLabel('ACTIVE')}
            items={active}
            pendingRoleId={pendingRoleId}
            onRelease={(id) => mutation.mutate({ roleId: id, action: 'release' })}
          />
          <GroupSection label={staffStatusLabel('REJECTED')} items={rejected} pendingRoleId={pendingRoleId} />
          {!hideReleased && (
            <GroupSection label={staffStatusLabel('RELEASED')} items={released} pendingRoleId={pendingRoleId} />
          )}
        </div>
      )}
    </Page>
  );
}
